/**
 * ForgotPassword — enter email, receive a reset link.
 * Response is always 200 regardless of email-existence (no user-enum leak).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email.trim()) { setError('Enter your email.'); return; }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err) {
      // Even on error the backend responds 200; this path is rare (network etc.)
      setError('Could not reach the server. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-4">
      <div className="w-full max-w-md border border-[#2F8A92]/15 rounded-lg p-6 md:p-8 bg-[#0A0A0A]" data-testid="forgot-password-page">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full border border-[#2F8A92]/30 flex items-center justify-center">
            <Mail size={22} className="text-[#2F8A92]" />
          </div>
        </div>
        <h1 className="font-historical text-[#D4A84A] text-2xl font-light tracking-tight text-center mb-1">
          Reset your password
        </h1>
        <p className="font-hud text-[10px] tracking-widest text-[#2F8A92] uppercase text-center mb-6">
          THE SHARED ORIGINS GLOBAL NETWORK
        </p>

        {!submitted ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1">
              <label className="font-hud text-[10px] tracking-widest uppercase text-white/80">EMAIL</label>
              <input
                type="email"
                autoFocus
                required
                data-testid="forgot-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="hud-input w-full"
              />
            </div>

            {error && <p className="font-mono text-xs text-red-400" data-testid="forgot-error">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-btn"
              className="w-full font-hud text-sm tracking-widest text-[#0A0A0A] bg-[#2F8A92] hover:bg-[#2F8A92]/85 py-3 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              SEND RESET LINK
            </button>

            <Link
              to="/"
              data-testid="forgot-back-link"
              className="flex items-center justify-center gap-2 font-hud text-[10px] tracking-widest text-white/80 hover:text-[#2F8A92] uppercase"
            >
              <ArrowLeft size={11} /> BACK TO LOGIN
            </Link>
          </form>
        ) : (
          <div className="space-y-4 text-center" data-testid="forgot-sent">
            <CheckCircle2 size={40} className="text-[#2F8A92] mx-auto" />
            <h2 className="font-historical text-[#D4A84A] text-xl font-light">Check your inbox.</h2>
            <p className="font-mono text-[11px] text-white/80 leading-relaxed">
              If <span className="text-[#D4A84A]">{email}</span> is registered with us, a reset link is on
              its way. The link expires in 15 minutes.
            </p>
            <p className="font-mono text-[10px] text-[#52525B]">
              Nothing in your inbox after a few minutes? Check spam, or try again.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-hud text-[10px] tracking-widest text-white/80 hover:text-[#2F8A92] uppercase"
            >
              <ArrowLeft size={11} /> BACK TO LOGIN
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
